import {
  AI_CSAT_COOLDOWN_MS,
  AI_CSAT_HANDLED_STORAGE_KEY,
  AI_CSAT_LAST_ASKED_STORAGE_KEY,
  AI_CSAT_PENDING_MAX_AGE_MS,
  AI_CSAT_PENDING_STORAGE_KEY,
  CSAT_RATED_MAX_ENTRIES,
} from '../constants/chat';
import { readIdList, rememberId } from './storedIdList';

// Decides when the AI chat CSAT popup (ChatCsatPopup) may be shown.
//
// Chatbot writes a single "pending" candidate after every assistant answer, so a
// conversation the customer leaves by killing the app still has a record on the
// next open. AiChatCsatHost reads it when the chat closes and when the app frame
// mounts. Any escalation to the team withdraws the candidate; the team CSAT
// covers that thread instead.
//
// Showing the popup consumes the candidate (so an app kill with the popup open
// cannot re-ask); the 14-day cooldown and the handled list are written only when
// the customer answers or dismisses, so a failed submit costs nothing.

const defaultStorage = () => (typeof localStorage === 'undefined' ? null : localStorage);

function readJson(storage, key, fallback) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
}

function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Full or blocked store: the customer is asked one time fewer, nothing else.
  }
}

function removeKey(storage, key) {
  try {
    storage.removeItem(key);
  } catch (error) {
    // Blocked store: the stale key is simply left behind.
  }
}

function readPending(storage) {
  const pending = readJson(storage, AI_CSAT_PENDING_STORAGE_KEY, null);
  if (!pending || !pending.conversationId || !Number.isFinite(pending.at)) {
    return null;
  }
  return pending;
}

function isHandled(conversationId, storage) {
  return readIdList(AI_CSAT_HANDLED_STORAGE_KEY, storage).includes(String(conversationId));
}

// Only the matching candidate: a missing id must not wipe another conversation's.
export function clearCandidate(conversationId, storage = defaultStorage()) {
  if (!storage || !conversationId) {
    return;
  }
  const pending = readPending(storage);
  if (pending && String(pending.conversationId) === String(conversationId)) {
    removeKey(storage, AI_CSAT_PENDING_STORAGE_KEY);
  }
}

export function recordCandidate(
  { conversationId, escalated = false },
  { storage = defaultStorage(), now = Date.now() } = {},
) {
  if (!storage || !conversationId) {
    return;
  }
  if (escalated) {
    clearCandidate(conversationId, storage);
    return;
  }
  if (isHandled(conversationId, storage)) {
    return;
  }
  writeJson(storage, AI_CSAT_PENDING_STORAGE_KEY, { conversationId: String(conversationId), at: now });
}

// Expired and already-handled candidates are dropped on the way; a candidate
// inside the cooldown is left in place and simply ages out, because the cooldown
// is longer than the max age.
export function eligibleCandidate({ storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage) {
    return null;
  }
  const pending = readPending(storage);
  if (!pending) {
    return null;
  }
  if (now - pending.at > AI_CSAT_PENDING_MAX_AGE_MS || isHandled(pending.conversationId, storage)) {
    removeKey(storage, AI_CSAT_PENDING_STORAGE_KEY);
    return null;
  }
  const lastAsked = Number(readJson(storage, AI_CSAT_LAST_ASKED_STORAGE_KEY, 0));
  if (Number.isFinite(lastAsked) && lastAsked > 0 && now - lastAsked < AI_CSAT_COOLDOWN_MS) {
    return null;
  }
  return { conversationId: String(pending.conversationId) };
}

// The customer answered or dismissed: start the cooldown, never ask about this
// conversation again.
export function markHandled(conversationId, { storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage) {
    return;
  }
  writeJson(storage, AI_CSAT_LAST_ASKED_STORAGE_KEY, now);
  rememberId(AI_CSAT_HANDLED_STORAGE_KEY, conversationId, CSAT_RATED_MAX_ENTRIES, storage);
  clearCandidate(conversationId, storage);
}
